/**
 * Admin — Action Buttons designer.
 *
 * Configures the estimate-results "Next Steps" bar: a Universal default set plus
 * optional per-retailer overrides (a retailer override fully replaces the
 * universal bar). Mirrors the Column Layout / Custom Tables admin pages.
 */

import React, { useCallback, useEffect, useState, lazy, Suspense } from 'react';
import {
  MousePointerClick, Plus, Trash2, ArrowUp, ArrowDown, Loader2, Save, RotateCcw,
  AlertTriangle, Pencil,
} from 'lucide-react';
import {
  fetchActionBars, saveActionBar, deleteActionBar,
  ActionButtonsListResponse,
} from '../../services/adminActionButtonsApi';
import {
  ActionButtonConfig, ActionSpec, ActionType, ActionButtonVariant,
  ACTION_TYPE_REGISTRY, defaultActionForType,
} from '../../config/actionButtons';
import { DynamicActionIcon } from '../../components/results/DynamicActionIcon';
import { IconPicker } from '../../components/admin/IconPicker';
import { TranslationFields } from '../../components/admin/TranslationFields';
import { ColorField } from '../../components/admin/ColorField';
import { saveTranslations, type TranslationValues } from '../../services/translationsApi';
import { fetchLocationMaps, type AdminLocationMapSummary } from '../../services/adminLocationMapsApi';

// Jodit is heavy — load the editor on demand so it stays out of the main app
// bundle and only downloads when a super-admin actually edits a modal button.
const RichTextEditor = lazy(() => import('../../components/admin/RichTextEditor'));

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v));

/** Buttons to edit for a scope: its own override, else universal, else defaults. */
function computeInitial(d: ActionButtonsListResponse, retailerId: number | null): ActionButtonConfig[] {
  const own = d.bars.find(b => b.retailerId === retailerId);
  if (own) return own.buttons;
  if (retailerId === null) return d.defaults;
  const universal = d.bars.find(b => b.retailerId === null);
  return universal ? universal.buttons : d.defaults;
}

export const AdminActionButtons: React.FC = () => {
  const [data, setData] = useState<ActionButtonsListResponse | null>(null);
  // Per-locale button labels, keyed 'buttons_json.#<button id>.label'. Held
  // here rather than inside TranslationFields so they save in the same submit
  // as the English — two Save buttons for one edit is how half-translated bars
  // happen.
  const [translations, setTranslations] = useState<TranslationValues>({});
  const [scope, setScope] = useState<number | null>(null); // null = universal
  const [draft, setDraft] = useState<ActionButtonConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [iconPickerIdx, setIconPickerIdx] = useState<number | null>(null);
  // Maps a "map" button can open (from /admin/maps). Loaded once; null until answered.
  const [maps, setMaps] = useState<AdminLocationMapSummary[] | null>(null);
  useEffect(() => { fetchLocationMaps().then(setMaps).catch(() => setMaps([])); }, []);

  const load = useCallback(async (targetScope: number | null) => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchActionBars();
      setData(d);
      setDraft(clone(computeInitial(d, targetScope)));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(null); }, [load]);

  const hasOverride = scope !== null && !!data?.bars.find(b => b.retailerId === scope);

  // The bar row translations attach to. Null while a retailer is inheriting the
  // universal bar: there is no row of their own yet, so there is nothing to key
  // translations to until they save an override.
  const currentBar = data?.bars.find(b => b.retailerId === scope) ?? null;

  // One translatable field per button, addressed BY ID so reordering the bar
  // doesn't move a translation onto its neighbour.
  const translatableFields = draft.map(b => ({
    path: `buttons_json.#${b.id}.label`,
    label: b.label || b.id,
    english: b.label,
  }));

  const onScopeChange = (val: string) => {
    const rid = val === 'universal' ? null : Number(val);
    setScope(rid);
    setMessage(null);
    setError(null);
    if (data) setDraft(clone(computeInitial(data, rid)));
  };

  // -- draft mutations --
  const updateBtn = (idx: number, patch: Partial<ActionButtonConfig>) =>
    setDraft(d => d.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  const updateAction = (idx: number, action: ActionSpec) => updateBtn(idx, { action });
  const move = (idx: number, dir: -1 | 1) =>
    setDraft(d => {
      const j = idx + dir;
      if (j < 0 || j >= d.length) return d;
      const n = [...d];
      [n[idx], n[j]] = [n[j], n[idx]];
      return n.map((b, i) => ({ ...b, order: i }));
    });
  const removeBtn = (idx: number) =>
    setDraft(d => d.filter((_, i) => i !== idx).map((b, i) => ({ ...b, order: i })));
  const addBtn = () =>
    setDraft(d => [...d, {
      id: `btn-${Date.now()}`,
      label: 'New Button',
      icon: { type: 'lucide', name: 'circle' },
      variant: 'default',
      order: d.length,
      enabled: true,
      requiresSavedEstimate: false,
      action: { type: 'link', url: '', target: '_blank' },
    }]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const ordered = draft.map((b, i) => ({ ...b, order: i }));
      const res = await saveActionBar(scope, ordered);
      setData(prev => {
        if (!prev) return prev;
        const exists = prev.bars.some(b => b.retailerId === res.bar.retailerId);
        return {
          ...prev,
          bars: exists
            ? prev.bars.map(b => (b.retailerId === res.bar.retailerId ? res.bar : b))
            : [...prev.bars, res.bar],
        };
      });
      setDraft(clone(res.bar.buttons));

      // Translations save AFTER the bar, because a first-time override has no
      // row id until the bar itself is created. Failing here is reported but
      // does not roll back the English — the bar is saved either way, and
      // losing that to a translation error would be the worse trade.
      try {
        await saveTranslations('estimate_action_bars', res.bar.id, translations);
        setMessage('Saved. Reload an estimate to see the change.');
      } catch {
        setMessage('Bar saved, but the translations could not be stored. Try saving again.');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const resetOrRemove = async () => {
    const msg = scope === null
      ? 'Reset the Universal bar to the built-in defaults?'
      : 'Remove this retailer override (revert to the Universal bar)?';
    if (!window.confirm(msg)) return;
    setSaving(true);
    setError(null);
    try {
      await deleteActionBar(scope);
      await load(scope);
      setMessage(scope === null ? 'Reset to defaults.' : 'Override removed.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const uploadRetailerId = scope ?? 0; // 0 = universal namespace for uploaded icons

  return (
    <div className="bg-white rounded-3 shadow-sm p-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h2 className="fw-bold mb-1 d-flex align-items-center gap-2">
            <MousePointerClick size={22} /> Action Buttons
          </h2>
          <p className="text-muted mb-0">
            The estimate results "Next Steps" bar. Configure the <strong>Universal</strong> default,
            or give a retailer its own set (a retailer override replaces the universal bar).
          </p>
        </div>
      </div>

      {/* Scope picker */}
      <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
        <div>
          <label className="form-label small fw-semibold mb-1">Editing</label>
          <select
            className="form-select form-select-sm"
            style={{ minWidth: 260 }}
            value={scope === null ? 'universal' : String(scope)}
            onChange={e => onScopeChange(e.target.value)}
            disabled={loading || saving}
          >
            <option value="universal">Universal (default set)</option>
            {data?.retailers.filter(r => r.id !== 1).map(r => {
              const overridden = !!data.bars.find(b => b.retailerId === r.id);
              return <option key={r.id} value={r.id}>{r.name}{overridden ? ' — custom' : ''}</option>;
            })}
          </select>
        </div>
        {scope !== null && !hasOverride && (
          <span className="text-muted small mb-1">No override yet — pre-filled from Universal. Save to create one.</span>
        )}
      </div>

      {error && (
        <div className="alert alert-warning py-2 px-3 small d-flex align-items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}
      {message && <div className="alert alert-success py-2 px-3 small">{message}</div>}

      {loading ? (
        <div className="text-muted small d-flex align-items-center gap-2 py-4">
          <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
        </div>
      ) : (
        <>
          {/* Live preview */}
          <div className="border rounded-3 p-3 mb-3 bg-light">
            <div className="small fw-semibold text-muted mb-2">Preview</div>
            <div className="d-flex flex-wrap gap-2">
              {draft.filter(b => b.enabled).map(b => (
                <span key={b.id} className={`btn btn-sm ${b.variant === 'primary' ? 'btn-primary' : b.variant === 'secondary' ? 'btn-secondary' : 'btn-outline-secondary'} d-inline-flex align-items-center gap-2`} style={{ pointerEvents: 'none' }}>
                  <DynamicActionIcon icon={b.icon} size={16} /> {b.label}
                </span>
              ))}
              {draft.filter(b => b.enabled).length === 0 && <span className="text-muted small">No enabled buttons.</span>}
            </div>
          </div>

          {/* Button editors */}
          {draft.map((btn, idx) => (
            <div key={btn.id} className="border rounded-3 p-3 mb-2">
              <div className="d-flex align-items-start gap-3">
                {/* Reorder */}
                <div className="btn-group-vertical btn-group-sm">
                  <button type="button" className="btn btn-outline-secondary" disabled={idx === 0} onClick={() => move(idx, -1)} title="Move up"><ArrowUp size={13} /></button>
                  <button type="button" className="btn btn-outline-secondary" disabled={idx === draft.length - 1} onClick={() => move(idx, 1)} title="Move down"><ArrowDown size={13} /></button>
                </div>

                {/* Icon */}
                <div className="text-center">
                  <div className="border rounded-3 d-inline-flex align-items-center justify-content-center mb-1" style={{ width: 48, height: 48 }}>
                    <DynamicActionIcon icon={btn.icon} size={24} />
                  </div>
                  <button type="button" className="btn btn-link btn-sm p-0 d-block mx-auto text-decoration-none" onClick={() => setIconPickerIdx(idx)}>
                    <Pencil size={11} /> Icon
                  </button>
                </div>

                {/* Fields */}
                <div className="flex-grow-1">
                  <div className="row g-2 mb-2">
                    <div className="col-sm-5">
                      <label className="form-label small fw-semibold mb-1">Label</label>
                      <input className="form-input" value={btn.label} maxLength={60} onChange={e => updateBtn(idx, { label: e.target.value })} />
                    </div>
                    <div className="col-sm-4">
                      <label className="form-label small fw-semibold mb-1">Action</label>
                      <select className="form-select form-select-sm" value={btn.action.type} onChange={e => updateAction(idx, defaultActionForType(e.target.value as ActionType))}>
                        {ACTION_TYPE_REGISTRY.map(a => (
                          <option key={a.type} value={a.type}>{a.label}{a.phase2 ? ' (soon)' : ''}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-sm-3">
                      <label className="form-label small fw-semibold mb-1">Style</label>
                      <select className="form-select form-select-sm" value={btn.variant} onChange={e => updateBtn(idx, { variant: e.target.value as ActionButtonVariant })}>
                        <option value="default">Default</option>
                        <option value="primary">Primary</option>
                        <option value="secondary">Secondary</option>
                      </select>
                    </div>
                  </div>

                  {/* Action-specific fields */}
                  {btn.action.type === 'link' && (
                    <div className="row g-2 mb-2">
                      <div className="col-sm-8">
                        <label className="form-label small mb-1">URL</label>
                        <input className="form-input" placeholder="https://… or /path or mailto:" value={btn.action.url} onChange={e => updateAction(idx, { ...btn.action, url: e.target.value } as ActionSpec)} />
                      </div>
                      <div className="col-sm-4">
                        <label className="form-label small mb-1">Opens</label>
                        <select className="form-select form-select-sm" value={btn.action.target} onChange={e => updateAction(idx, { ...btn.action, target: e.target.value as '_self' | '_blank' } as ActionSpec)}>
                          <option value="_blank">New tab</option>
                          <option value="_self">Same tab</option>
                        </select>
                      </div>
                    </div>
                  )}
                  {btn.action.type === 'modal' && (
                    <div className="mb-2">
                      <label className="form-label small mb-1">Modal title</label>
                      <input className="form-input mb-2" value={btn.action.title ?? ''} maxLength={120} onChange={e => updateAction(idx, { ...btn.action, title: e.target.value } as ActionSpec)} />
                      <label className="form-label small mb-1">Content (use the <code>&lt;/&gt;</code> button to edit HTML source)</label>
                      <Suspense fallback={<div className="text-muted small py-3"><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading editor…</div>}>
                        <RichTextEditor
                          key={`${scope ?? 'u'}-${btn.id}`}
                          value={btn.action.contentHtml}
                          onChange={html => updateAction(idx, { ...btn.action, contentHtml: html } as ActionSpec)}
                        />
                      </Suspense>
                      <div className="row g-2 mt-2 align-items-end">
                        <div className="col-sm-3">
                          <label className="form-label small mb-1">Size</label>
                          <select className="form-select form-select-sm" value={btn.action.size ?? 'md'} onChange={e => updateAction(idx, { ...btn.action, size: e.target.value as 'sm' | 'md' | 'lg' | 'xl' } as ActionSpec)}>
                            <option value="sm">Small</option>
                            <option value="md">Medium</option>
                            <option value="lg">Large</option>
                            <option value="xl">Extra large</option>
                          </select>
                        </div>
                        <div className="col-sm-4">
                          <ColorField label="Title bar background" value={btn.action.headerBg} onChange={v => updateAction(idx, { ...btn.action, headerBg: v } as ActionSpec)} />
                        </div>
                        <div className="col-sm-4">
                          <ColorField label="Title bar text" value={btn.action.headerFg} onChange={v => updateAction(idx, { ...btn.action, headerFg: v } as ActionSpec)} />
                        </div>
                      </div>
                    </div>
                  )}
                  {btn.action.type === 'map' && (
                    <div className="mb-2">
                      <div className="row g-2 align-items-end">
                        <div className="col-sm-5">
                          <label className="form-label small mb-1">Map</label>
                          <select className="form-select form-select-sm" value={btn.action.mapId || ''} onChange={e => updateAction(idx, { ...btn.action, mapId: Number(e.target.value) || 0 } as ActionSpec)}>
                            <option value="">— choose a map —</option>
                            {(maps ?? []).map(m => (
                              <option key={m.id} value={m.id} disabled={!m.isActive}>{m.name} ({m.dataset}){m.isActive ? '' : ' — inactive'}</option>
                            ))}
                          </select>
                          <div className="form-text">Maps are designed under <a href="/admin/maps">Location Maps</a>.</div>
                        </div>
                        <div className="col-sm-4">
                          <label className="form-label small mb-1">Modal title</label>
                          <input className="form-input" value={btn.action.title ?? ''} maxLength={120} placeholder="Locations near you" onChange={e => updateAction(idx, { ...btn.action, title: e.target.value } as ActionSpec)} />
                        </div>
                        <div className="col-sm-3">
                          <label className="form-label small mb-1">Size</label>
                          <select className="form-select form-select-sm" value={btn.action.size ?? 'xl'} onChange={e => updateAction(idx, { ...btn.action, size: e.target.value as 'lg' | 'xl' } as ActionSpec)}>
                            <option value="lg">Large</option>
                            <option value="xl">Extra large</option>
                          </select>
                        </div>
                      </div>
                      <div className="row g-2 mt-1">
                        <div className="col-sm-4">
                          <ColorField label="Title bar background" value={btn.action.headerBg} onChange={v => updateAction(idx, { ...btn.action, headerBg: v } as ActionSpec)} />
                        </div>
                        <div className="col-sm-4">
                          <ColorField label="Title bar text" value={btn.action.headerFg} onChange={v => updateAction(idx, { ...btn.action, headerFg: v } as ActionSpec)} />
                        </div>
                      </div>
                      {maps !== null && maps.length === 0 && (
                        <div className="form-text text-warning">No maps exist yet — create one under Location Maps first.</div>
                      )}
                    </div>
                  )}
                  {btn.action.type === 'ajax' && (
                    <div className="mb-2">
                      <label className="form-label small mb-1">Endpoint id (server allowlist)</label>
                      <input className="form-input" value={btn.action.endpointId} onChange={e => updateAction(idx, { ...btn.action, endpointId: e.target.value } as ActionSpec)} />
                      <div className="form-text text-warning">Webhook actions aren't wired at runtime yet (coming soon).</div>
                    </div>
                  )}

                  <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="form-check form-switch m-0 small">
                      <input type="checkbox" className="form-check-input" role="switch" checked={btn.enabled} onChange={e => updateBtn(idx, { enabled: e.target.checked })} id={`en-${idx}`} />
                      <label className="form-check-label" htmlFor={`en-${idx}`}>Enabled</label>
                    </div>
                    <div className="form-check form-switch m-0 small">
                      <input type="checkbox" className="form-check-input" role="switch" checked={!!btn.requiresSavedEstimate} onChange={e => updateBtn(idx, { requiresSavedEstimate: e.target.checked })} id={`rs-${idx}`} />
                      <label className="form-check-label" htmlFor={`rs-${idx}`}>Only after the estimate is saved</label>
                    </div>
                    <button type="button" className="btn btn-link btn-sm text-danger p-0 ms-auto" onClick={() => removeBtn(idx)}>
                      <Trash2 size={13} /> Remove
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Per-locale labels for this bar. Keyed by button id, so reordering
              above cannot move a translation onto the wrong button. */}
          <TranslationFields
            entityType="estimate_action_bars"
            entityId={currentBar?.id ?? null}
            fields={translatableFields}
            values={translations}
            onChange={setTranslations}
            title="Button labels in other languages"
          />

          <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
            <button type="button" className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1" onClick={addBtn} disabled={saving}>
              <Plus size={14} /> Add button
            </button>
            <button type="button" className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1 ms-auto" onClick={save} disabled={saving}>
              {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
              Save {scope === null ? 'Universal' : 'retailer'} bar
            </button>
            {(scope === null || hasOverride) && (
              <button type="button" className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1" onClick={resetOrRemove} disabled={saving}>
                <RotateCcw size={14} /> {scope === null ? 'Reset to defaults' : 'Remove override'}
              </button>
            )}
          </div>
        </>
      )}

      {iconPickerIdx !== null && (
        <IconPicker
          retailerId={uploadRetailerId}
          onSelect={icon => updateBtn(iconPickerIdx, { icon })}
          onClose={() => setIconPickerIdx(null)}
        />
      )}
    </div>
  );
};

export default AdminActionButtons;
